import { get, set } from 'idb-keyval';

export interface GlobalModifier {
  name: string;
  image?: string;
  price?: number;
  visible: boolean;
  productCount: number;
}

export interface GlobalModifiersMap {
  [modifierName: string]: {
    image?: string;
    price?: number;
    visible: boolean;
  };
}

const GLOBAL_MODIFIERS_KEY = 'globalModifiers';

export async function saveGlobalModifiers(modifiers: GlobalModifiersMap): Promise<void> {
  try {
    await set(GLOBAL_MODIFIERS_KEY, modifiers);
    localStorage.setItem(GLOBAL_MODIFIERS_KEY, JSON.stringify(modifiers));
    console.log('[GlobalModifiersService] Global modifiers saved:', modifiers);
  } catch (error) {
    console.error('[GlobalModifiersService] Error saving global modifiers:', error);
    throw error;
  }
}

export async function loadGlobalModifiers(): Promise<GlobalModifiersMap> {
  try {
    const modifiers = (await get(GLOBAL_MODIFIERS_KEY)) || {};
    console.log('[GlobalModifiersService] Global modifiers loaded:', modifiers);
    return modifiers;
  } catch (error) {
    console.error('[GlobalModifiersService] Error loading global modifiers:', error);
    return {};
  }
}

export async function updateGlobalModifier(
  name: string,
  data: { image?: string; price?: number; visible?: boolean }
): Promise<void> {
  try {
    const modifiers = await loadGlobalModifiers();
    modifiers[name] = {
      ...(modifiers[name] || { visible: true }),
      ...data,
    };
    await saveGlobalModifiers(modifiers);
  } catch (error) {
    console.error('[GlobalModifiersService] Error updating global modifier:', error);
    throw error;
  }
}

export async function deleteGlobalModifier(name: string): Promise<void> {
  try {
    const modifiers = await loadGlobalModifiers();
    delete modifiers[name];
    await saveGlobalModifiers(modifiers);
  } catch (error) {
    console.error('[GlobalModifiersService] Error deleting global modifier:', error);
    throw error;
  }
}

export function extractAllModifiers(products: any[]): Map<string, number> {
  const modifiersCount = new Map<string, number>();

  products.forEach((product) => {
    if (product.specifications) {
      product.specifications.forEach((spec: any) => {
        if (spec.list) {
          spec.list.forEach((option: any) => {
            const name = option.name?.[0];
            if (name) {
              modifiersCount.set(name, (modifiersCount.get(name) || 0) + 1);
            }
          });
        }
      });
    }
  });

  return modifiersCount;
}

export async function applyGlobalModifiersToProducts(
  products: any[],
  subproductSettings: any
): Promise<any[]> {
  try {
    const globalModifiers = await loadGlobalModifiers();

    return products.map((product) => {
      if (!product.specifications) return product;

      const updatedSpecs = product.specifications.map((spec: any) => {
        if (!spec.list) return spec;

        const updatedList = spec.list.map((option: any) => {
          const name = option.name?.[0];
          if (!name) return option;

          const hasSpecificSettings = subproductSettings[product._id]?.[name];

          if (hasSpecificSettings) {
            return option;
          }

          const globalModifier = globalModifiers[name];
          if (globalModifier) {
            return {
              ...option,
              image: globalModifier.image || option.image,
              price: globalModifier.price !== undefined ? globalModifier.price : option.price,
            };
          }

          return option;
        });

        return { ...spec, list: updatedList };
      });

      return { ...product, specifications: updatedSpecs };
    });
  } catch (error) {
    console.error('[GlobalModifiersService] Error applying global modifiers:', error);
    return products;
  }
}

export function getModifierSource(
  productId: string,
  modifierName: string,
  globalModifiers: GlobalModifiersMap,
  subproductSettings: any
): 'specific' | 'global' | 'default' {
  if (subproductSettings[productId]?.[modifierName]) {
    return 'specific';
  }

  if (globalModifiers[modifierName]) {
    return 'global';
  }

  return 'default';
}
